import React, { useEffect, useState, useRef } from 'react';
import { productService } from '../services/product';
import { Product } from '../types';
import ProductCard from './ProductCard';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import 'swiper/css';

const HomeProductsShowcase: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [bestSellingProducts, setBestSellingProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const swiperRef = useRef<SwiperType | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const featuredResponse = await productService.getFeatured();
        setFeaturedProducts(featuredResponse.results || []);
        const bestSellingResponse = await productService.getBestSelling();
        setBestSellingProducts(bestSellingResponse.results || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <div>
      {/* Lo más vendido */}
      <section className="py-16 bg-background-light dark:bg-background-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-5xl text-center mb-12">Lo más vendido</h2>
          {loading ? (
            <div className="text-center py-8">Cargando...</div>
          ) : (
            <>
              {/* Vista Desktop - Carrusel */}
              <div className="hidden md:block relative">
                <Swiper
                  modules={[Autoplay]}
                  loop={true}
                  autoplay={{ delay: 3000, disableOnInteraction: true, pauseOnMouseEnter: true }}
                  spaceBetween={16}
                  breakpoints={{
                    320: { slidesPerView: 1 },
                    640: { slidesPerView: 2 },
                    1024: { slidesPerView: 4 },
                  }}
                  onSwiper={(swiper: SwiperType) => { swiperRef.current = swiper; }}
                  className="pb-8"
                >
                  {bestSellingProducts.map((product) => (
                    <SwiperSlide key={product.id}>
                      <div className="px-2">
                        <ProductCard product={product} />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Flecha izquierda personalizada */}
                <button
                  type="button"
                  aria-label="Anterior"
                  onClick={(e) => {
                    e.preventDefault();
                    swiperRef.current?.slidePrev();
                  }}
                  className="absolute left-0 -translate-x-4 md:-translate-x-8 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 border border-gray-200 hover:border-pink-dark group"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700 group-hover:text-pink-dark transition-colors" />
                </button>

                {/* Flecha derecha personalizada */}
                <button
                  type="button"
                  aria-label="Siguiente"
                  onClick={(e) => {
                    e.preventDefault();
                    swiperRef.current?.slideNext();
                  }}
                  className="absolute right-0 translate-x-4 md:translate-x-8 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 border border-gray-200 hover:border-pink-dark group"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700 group-hover:text-pink-dark transition-colors" />
                </button>
              </div>

              {/* Vista Móvil - Grid compacto */}
              <div className="md:hidden grid grid-cols-2 gap-0">
                {bestSellingProducts.slice(0, 8).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomeProductsShowcase;